import type { AccessNodePublic } from '@accounts/shared';

import { STR } from '../../../../i18n';

import { chainOf, childrenOf } from './hierarchy';

/**
 * "Filed under": one picker per step down the tree, each able to stop (FINAL demo,
 * `filedUnderFields`). The first picker offers the top-level entries, each next one the
 * children of the entry picked above it — so a row can stop at any level, and a choice the
 * server would refuse (a node that is not below the one above it) is never offered.
 *
 * The value is ONE node id (or null for "not filed"): the row's place is a single node and
 * its path is derived from it, never stored as a second list of ids.
 */
export function FiledUnderPicker({
  nodes,
  value,
  onChange,
  disabled = false,
  idPrefix,
}: {
  nodes: readonly AccessNodePublic[];
  value: string | null;
  onChange: (nodeId: string | null) => void;
  disabled?: boolean;
  idPrefix: string;
}) {
  const chain = chainOf(nodes, value);
  const pickers: { index: number; options: AccessNodePublic[]; selected: string }[] = [];

  for (let index = 0; ; index += 1) {
    const parentId = index === 0 ? null : (chain[index - 1] ?? null);
    if (index > 0 && parentId === null) break;
    const options = childrenOf(nodes, parentId);
    const selected = chain[index] ?? '';
    // A retired node already on the row's path stays visible in its picker, so the
    // control shows the row's real place rather than silently reading "not filed".
    const current = nodes.find((node) => node.id === selected);
    if (current !== undefined && !options.some((option) => option.id === selected)) {
      options.unshift(current);
    }
    if (options.length === 0) break;
    pickers.push({ index, options, selected });
    if (selected === '') break;
  }

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      {pickers.map((picker) => {
        const levels = new Set(picker.options.map((option) => option.level.name));
        const [onlyLevel] = levels;
        const label =
          levels.size === 1 && onlyLevel !== undefined
            ? onlyLevel
            : STR.access.catalog.pickerLevel(picker.index + 1);
        const id = `${idPrefix}-step-${String(picker.index)}`;
        return (
          <div key={picker.index} style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            {picker.index > 0 ? (
              <span className="tx-faint" aria-hidden="true" style={{ paddingBottom: 9 }}>
                {STR.access.catalog.pickerStep}
              </span>
            ) : null}
            <label htmlFor={id} style={{ display: 'block', minWidth: 150 }}>
              <span
                className="tx-subtle"
                style={{ display: 'block', fontSize: 11, fontWeight: 650, marginBottom: 4 }}
              >
                {label}
              </span>
              <select
                id={id}
                className="field"
                value={picker.selected}
                disabled={disabled}
                onChange={(event) => {
                  const next = event.target.value;
                  if (next === '') onChange(chain[picker.index - 1] ?? null);
                  else onChange(next);
                }}
                style={{
                  width: '100%',
                  padding: '7px 28px 7px 10px',
                  borderRadius: 8,
                  fontSize: 12.5,
                }}
              >
                <option value="">
                  {picker.index === 0 ? STR.access.catalog.pickerNone : STR.access.catalog.pickerStop}
                </option>
                {picker.options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        );
      })}
    </div>
  );
}
